const Util = require('@antv/f2/lib/util/common')
const Global = require('@antv/f2/lib/global')
const Tooltip = require('@antv/f2/lib/component/tooltip')
const Helper = require('@antv/f2/lib/util/helper')
const TooltipPluginOrigin = require('@antv/f2/lib/plugin/tooltip')

// Register the default configuration for Tooltip
Global.tooltip = Util.deepMix({
  triggerOn: ['touchstart', 'touchmove'],
  // triggerOff: 'touchend',
  alwaysShow: false,
  showTitle: false,
  showCrosshairs: false,
  crosshairsStyle: {
    stroke: 'rgba(0, 0, 0, 0.25)',
    lineWidth: 1
  },
  showTooltipMarker: true,
  background: {
    radius: 1,
    fill: 'rgba(0, 0, 0, 0.65)',
    padding: [3, 5]
  },
  titleStyle: {
    fontSize: 12,
    fill: '#fff',
    textAlign: 'start',
    textBaseline: 'top'
  },
  nameStyle: {
    fontSize: 12,
    fill: 'rgba(255, 255, 255, 0.65)',
    textAlign: 'start',
    textBaseline: 'middle'
  },
  valueStyle: {
    fontSize: 12,
    fill: '#fff',
    textAlign: 'start',
    textBaseline: 'middle'
  },
  showItemMarker: true,
  itemMarkerStyle: {
    radius: 3,
    symbol: 'circle',
    lineWidth: 1,
    stroke: '#fff'
  },
  layout: 'horizontal',
  snap: false
}, Global.tooltip || {})

function _getTooltipValueScale(geom) {
  const colorAttr = geom.getAttr('color')
  if (colorAttr) {
    const colorScale = colorAttr.getScale(colorAttr.type)
    if (colorScale.isLinear) {
      return colorScale
    }
  }
  const xScale = geom.getXScale()
  const yScale = geom.getYScale()
  if (yScale) {
    return yScale
  }

  return xScale
}

function getTooltipName(geom, origin) {
  let name
  let nameScale
  const groupScales = geom._getGroupScales()
  if (groupScales.length) {
    Util.each(groupScales, (scale) => {
      nameScale = scale
      return false
    })
  }
  if (nameScale) {
    const { field } = nameScale
    name = nameScale.getText(origin[field])
  } else {
    const valueScale = _getTooltipValueScale(geom)
    name = valueScale.alias || valueScale.field
  }
  return name
}

function getTooltipValue(geom, origin) {
  const scale = _getTooltipValueScale(geom)
  return scale.getText(origin[scale.field])
}

function getTooltipTitle(geom, origin) {
  const position = geom.getAttr('position')
  const field = position.getFields()[0]
  const scale = geom.get('scales')[field]
  return scale.getText(origin[scale.field])
}

function _indexOfArray(items, item) {
  let rst = -1
  Util.each(items, (sub, index) => {
    if (sub.title === item.title && sub.name === item.name && sub.value === item.value && sub.color === item.color) {
      rst = index
      return false
    }
    return undefined
  })
  return rst
}

function _uniqItems(items) {
  const tmp = []
  Util.each(items, (item) => {
    const index = _indexOfArray(tmp, item)
    if (index === -1) {
      tmp.push(item)
    } else {
      tmp[index] = item
    }
  })
  return tmp
}

function isEqual(arr1, arr2) {
  return JSON.stringify(arr1) === JSON.stringify(arr2)
}

class TooltipController {
  constructor(cfg) {
    this.enable = true
    this.cfg = {}
    this.tooltip = null
    this.chart = null
    this.timeStamp = 0
    Util.mix(this, cfg)
    const { chart } = this
    this.canvasDom = chart.get('canvas').get('el')
  }

  _setCrosshairsCfg() {
    const self = this
    const { chart } = self
    const defaultCfg = Util.mix({}, Global.tooltip)
    const geoms = chart.get('geoms')
    const shapes = []
    Util.each(geoms, (geom) => {
      const type = geom.get('type')
      if (shapes.indexOf(type) === -1) {
        shapes.push(type)
      }
    })
    const coordType = chart.get('coord').type
    if (geoms.length && (coordType === 'cartesian' || coordType === 'rect') && shapes.length === 1 && ['line', 'area', 'path', 'point'].indexOf(shapes[0]) !== -1) {
      Util.mix(defaultCfg, {
        showCrosshairs: true
      })
    }

    return defaultCfg
  }

  _getMaxLength(cfg = {}) {
    const { layout, plotRange } = cfg
    return (layout === 'horizontal') ? plotRange.br.x - plotRange.bl.x : plotRange.bl.y - plotRange.tr.y
  }

  render() {
    const self = this

    if (self.tooltip) {
      return
    }

    const { chart } = self
    const canvas = chart.get('canvas')
    const frontPlot = chart.get('frontPlot').addGroup({
      className: 'tooltipContainer',
      zIndex: 10
    })
    const backPlot = chart.get('backPlot').addGroup({
      className: 'tooltipContainer'
    })
    const plotRange = chart.get('plotRange')
    const coord = chart.get('coord')

    const defaultCfg = self._setCrosshairsCfg()
    const { cfg } = self // 通过 chart.tooltip() 接口传入的 tooltip 配置项
    const tooltipCfg = Util.deepMix({
      plotRange,
      frontPlot,
      backPlot,
      canvas,
      fixed: coord.transposed || coord.isPolar
    }, defaultCfg, cfg) // 创建 tooltip 实例需要的配置，不应该修改 this.cfg，即用户传入的配置
    tooltipCfg.maxLength = self._getMaxLength(tooltipCfg)
    this._tooltipCfg = tooltipCfg
    self.tooltip = new Tooltip(tooltipCfg)
    self.bindEvents()
  }

  clear() {
    const { tooltip } = this
    tooltip && tooltip.destroy()
    this.tooltip = null
    this.prePoint = null
    this._lastActive = null
    this.unBindEvents()
  }

  _getTooltipMarkerStyle(cfg = {}) {
    const { type, items } = cfg
    const tooltipCfg = this._tooltipCfg
    if (type === 'rect') {
      let x
      let y
      let width
      let height
      const { chart } = this
      const { tl, br } = chart.get('plotRange')
      const coord = chart.get('coord')
      const firstItem = items[0]
      const lastItem = items[items.length - 1]
      const intervalWidth = firstItem.width
      if (coord.transposed) {
        // eslint-disable-next-line prefer-destructuring
        x = tl.x
        y = lastItem.y - (intervalWidth * 0.75)
        width = br.x - tl.x
        height = firstItem.y - lastItem.y + (1.5 * intervalWidth)
      } else {
        x = firstItem.x - (intervalWidth * 0.75)
        y = tl.y
        width = lastItem.x - firstItem.x + (1.5 * intervalWidth)
        height = br.y - tl.y
      }

      cfg.style = Util.mix({
        x,
        y,
        width,
        height,
        fill: '#CCD6EC',
        opacity: 0.3
      }, tooltipCfg.tooltipMarkerStyle)
    } else {
      cfg.style = Util.mix({
        radius: 4,
        fill: '#fff',
        lineWidth: 2
      }, tooltipCfg.tooltipMarkerStyle)
    }

    return cfg
  }

  /**
   * @param point
   * @param items
   * @param tooltipMarkerCfg
   * @param isRepaint 添加新参数，如果 isRepaint 则不会调用 canvas draw, 只是会设置位置信息，优化性能
   * @private
   */
  _setTooltip(point, items, tooltipMarkerCfg = {}, isRepaint = false) {
    const lastActive = this._lastActive
    const { tooltip, chart } = this
    const cfg = this._tooltipCfg
    items = _uniqItems(items)

    const coord = chart.get('coord')
    const yScale = chart.getYScales()[0]
    const { snap } = cfg

    if (snap === false && yScale.isLinear) {
      const invertPoint = coord.invertPoint(point)
      const plot = chart.get('plotRange')

      let tip
      let pos
      if (Helper.isPointInPlot(point, plot)) {
        if (coord.transposed) {
          tip = yScale.invert(invertPoint.x)
          pos = point.x
          tooltip.setXTipContent(tip)
          tooltip.setXTipPosition(pos)
          tooltip.setYCrosshairPosition(pos)
        } else {
          tip = yScale.invert(invertPoint.y)
          pos = point.y
          tooltip.setYTipContent(tip)
          tooltip.setYTipPosition(pos)
          tooltip.setXCrosshairPosition(pos)
        }
      }
    }


    if (cfg.onShow) {
      cfg.onShow({
        x: point.x,
        y: point.y,
        tooltip,
        items,
        tooltipMarkerCfg
      })
    }
    if (isEqual(lastActive, items)) {
      if (snap === false && (Util.directionEnabled(cfg.crosshairsType, 'y') || cfg.showYTip) && !isRepaint) {
        const canvas = this.chart.get('canvas')
        canvas.draw()
      }
      return
    }
    this._lastActive = items

    const { onChange } = cfg
    if (onChange) {
      onChange({
        x: point.x,
        y: point.y,
        tooltip,
        items,
        tooltipMarkerCfg
      })
    }

    const geoms = chart.get('geoms')
    const types = geoms.map(geom => geom.get('type'))
    const doubleAxis = types.includes('interval') && (types.includes('line') || types.includes('path') || types.includes('area'))

    const first = items[0]
    const title = first.title || first.name
    let xTipPosX = first.x
    if (items.length > 1) {
      let last = items[items.length - 1]
      if (doubleAxis) {
        let i = items.length - 1
        while (i >= 0) {
          last = items[i]
          if (last.geomType === 'interval') {
            break
          }
          i--
        }
      }
      xTipPosX = (items[0].x + last.x) / 2
    }
    tooltip.setContent(title, items, coord.transposed)
    tooltip.setPosition(items, point)

    if (coord.transposed) {
      let yTipPosY = first.y
      if (items.length > 1) {
        let last = items[items.length - 1]
        if (doubleAxis) {
          let i = items.length - 1
          while (i >= 0) {
            last = items[i]
            if (last.geomType === 'interval') {
              break
            }
            i--
          }
        }
        yTipPosY = (items[0].y + last.y) / 2
      }
      tooltip.setYTipContent(title)
      tooltip.setYTipPosition(yTipPosY)
      tooltip.setXCrosshairPosition(yTipPosY)

      if (snap) {
        tooltip.setXTipContent(first.value)
        tooltip.setXTipPosition(xTipPosX)
        tooltip.setYCrosshairPosition(xTipPosX)
      }
    } else {
      tooltip.setXTipContent(title)
      tooltip.setXTipPosition(xTipPosX)
      tooltip.setYCrosshairPosition(xTipPosX)

      if (snap) {
        tooltip.setYTipContent(first.value)
        tooltip.setYTipPosition(first.y)
        tooltip.setXCrosshairPosition(first.y)
      }
    }

    const markerItems = tooltipMarkerCfg.items
    if (cfg.showTooltipMarker && markerItems.length) {
      tooltipMarkerCfg = this._getTooltipMarkerStyle(tooltipMarkerCfg)
      tooltip.setMarkers(tooltipMarkerCfg)
    } else {
      tooltip.clearMarkers()
    }

    if (isRepaint) {
      const {
        crosshairsShapeX,
        crosshairsShapeY,
        markerGroup,
        container,
        tooltipArrow,
        xTipBox,
        yTipBox,
      } = tooltip
      crosshairsShapeX && crosshairsShapeX.show()
      crosshairsShapeY && crosshairsShapeY.show()
      markerGroup && markerGroup.show()
      container && container.show()
      tooltipArrow && tooltipArrow.show()
      xTipBox && xTipBox.show()
      yTipBox && yTipBox.show()
    } else {
      tooltip.show()
    }
  }

  /**
   *
   * @param point
   * @param isRepaint 添加新参数，如果 isRepaint 则不会调用 canvas draw, 只是会设置位置信息，优化性能
   */
  showTooltip(point, isRepaint = false) {
    const self = this
    const { chart } = self

    let tooltipMarkerType
    const tooltipMarkerItems = []
    const items = []
    const cfg = self._tooltipCfg
    let marker
    if (cfg.showItemMarker) {
      marker = cfg.itemMarkerStyle
    }

    const geoms = chart.get('geoms')
    const coord = chart.get('coord')
    const types = geoms.map(geom => geom.get('type'))
    const doubleAxis = types.includes('interval') && (types.includes('line') || types.includes('path') || types.includes('area'))

    Util.each(geoms, (geom) => {
      if (geom.get('visible')) {
        const type = geom.get('type')
        const records = geom.getSnapRecords(point)
        Util.each(records, (record, index) => {
          // 不排除 null 值
          const { _origin, color } = record
          let { x, y } = record
          // 数据为 null 时 x,y 不存在，但是 tooltip 的指标器需要正确的位置
          if (x === undefined || y === undefined) {
            const itemPoint = chart.getPosition(_origin)
            // eslint-disable-next-line prefer-destructuring
            x = itemPoint.x
            // eslint-disable-next-line prefer-destructuring
            y = itemPoint.y

            // 当第一个为空值或者最后一个为空值时，需要重新修正 x,y 的值
            // 超出一个值时中点计算方法是第一个加上最后一个除以 2
            // 如果第一个和最后一个值都为空时无需修正
            if ((index === 0 || index === records.length - 1) && records.length > 1) {
              const { x: otherX, y: otherY } = records[records.length - 1 - index]
              if (!Number.isNaN(x) && (typeof otherX === 'number') && !Number.isNaN(otherX)) {
                x = (2 * x) - otherX
              }
              if (!Number.isNaN(y) && (typeof otherY === 'number') && !Number.isNaN(otherY)) {
                y = (2 * y) - otherY
              }
            }
          }

          const tooltipItem = {
            x,
            y: Util.isArray(y) ? y[1] : y,
            color: color || Global.defaultColor,
            origin: _origin,
            name: getTooltipName(geom, _origin),
            value: getTooltipValue(geom, _origin),
            title: getTooltipTitle(geom, _origin),
            geomType: type,
          }
          if (marker) {
            tooltipItem.marker = Util.mix({
              fill: color || Global.defaultColor
            }, marker)
          }
          items.push(tooltipItem)

          if (['line', 'area', 'path'].indexOf(type) !== -1) {
            tooltipMarkerType = 'circle'
            tooltipMarkerItems.push(tooltipItem)
          } else if (!doubleAxis && type === 'interval' && (coord.type === 'cartesian' || coord.type === 'rect')) {
            tooltipMarkerType = 'rect'
            tooltipItem.width = geom.getSize(record._origin)
            tooltipMarkerItems.push(tooltipItem)
          }
        })
      }
    })

    if (items.length) {
      const tooltipMarkerCfg = {
        items: tooltipMarkerItems,
        type: tooltipMarkerType
      }
      self._setTooltip(point, items, tooltipMarkerCfg, isRepaint)
    } else {
      self.hideTooltip()
    }
  }

  hideTooltip() {
    const cfg = this._tooltipCfg
    this._lastActive = null
    const { tooltip } = this
    if (tooltip) {
      tooltip.hide()
      if (cfg.onHide) {
        cfg.onHide({
          tooltip
        })
      }
      const canvas = this.chart.get('canvas')
      canvas.draw()
    }
  }

  handleShowEvent(ev) {
    const { chart } = this
    if (!this.enable || chart.get('_closeTooltip')) return

    const plot = chart.get('plotRange')
    const point = Util.createEvent(ev, chart)
    if (!Helper.isPointInPlot(point, plot) && !this._tooltipCfg.alwaysShow) { // not in chart plot
      this.hideTooltip()
      return
    }

    const lastTimeStamp = this.timeStamp
    const timeStamp = +new Date()
    if ((timeStamp - lastTimeStamp) > 16) {
      this.showTooltip(point)
      this.timeStamp = timeStamp
    }
  }

  handleHideEvent() {
    const { chart } = this
    if (!this.enable || chart.get('_closeTooltip')) return

    this.hideTooltip()
  }

  handleDocEvent(ev) {
    const { chart } = this
    if (!this.enable || chart.get('_closeTooltip')) return


    const { canvasDom } = this
    if (ev.target !== canvasDom) {
      this.hideTooltip()
    }
  }

  _handleEvent(methodName, method, action) {
    const { canvasDom } = this
    Util.each([].concat(methodName), (aMethod) => {
      if (action === 'bind') {
        Util.addEventListener(canvasDom, aMethod, method)
      } else {
        Util.removeEventListener(canvasDom, aMethod, method)
      }
    })
  }

  bindEvents() {
    const cfg = this._tooltipCfg || {}
    const { triggerOn, triggerOff, alwaysShow } = cfg
    const showMethod = Util.wrapBehavior(this, 'handleShowEvent')
    const hideMethod = Util.wrapBehavior(this, 'handleHideEvent')

    triggerOn && this._handleEvent(triggerOn, showMethod, 'bind')
    triggerOff && this._handleEvent(triggerOff, hideMethod, 'bind')
    // TODO: 当用户点击 canvas 外的事件时 tooltip 消失
    if (!alwaysShow) {
      const docMethod = Util.wrapBehavior(this, 'handleDocEvent')
      Util.isBrowser && Util.addEventListener(document, 'touchstart', docMethod)
    }
  }

  unBindEvents() {
    const cfg = this._tooltipCfg || {}
    const { triggerOn, triggerOff, alwaysShow } = cfg
    const showMethod = Util.getWrapBehavior(this, 'handleShowEvent')
    const hideMethod = Util.getWrapBehavior(this, 'handleHideEvent')

    triggerOn && this._handleEvent(triggerOn, showMethod, 'unBind')
    triggerOff && this._handleEvent(triggerOff, hideMethod, 'unBind')

    if (!alwaysShow) {
      const docMethod = Util.getWrapBehavior(this, 'handleDocEvent')
      Util.isBrowser && Util.removeEventListener(document, 'touchstart', docMethod)
    }
  }
}

const TooltipPlugin = {
  init(chart) {
    const tooltipController = new TooltipController({
      chart
    })
    chart.set('tooltipController', tooltipController)

    chart.tooltip = function (enable, cfg) {
      if (Util.isObject(enable)) {
        cfg = enable
        enable = true
      }
      tooltipController.enable = enable
      if (cfg) {
        tooltipController.cfg = cfg
      }
      return this
    }
  },
  afterGeomDraw(chart) {
    const tooltipController = chart.get('tooltipController')
    tooltipController.render()

    chart.showTooltip = function (point) {
      tooltipController.showTooltip(point)
      return this
    }

    chart.hideTooltip = function () {
      tooltipController.hideTooltip()
      return this
    }
  },
  clearInner(chart) {
    const tooltipController = chart.get('tooltipController')
    tooltipController.clear()
  }
}

export default (F2) => {
  F2.Chart.plugins.unregister(TooltipPluginOrigin)
  F2.Chart.plugins.register(TooltipPlugin)
}
